import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CatalogReportClient, Feature } from 'src/WebApiController';

/** Composite key for a reported section: a section is identified by its venue
 * config + name (case-insensitive), so every unmapped listing that matches shows
 * the "requested" state, not just the exact listing that was reported. */
const sectionKey = (
  venueConfigId: number | null | undefined,
  section: string | null | undefined
): string | null => {
  if (venueConfigId == null || !section || section.trim() === '') {
    return null;
  }
  return `${venueConfigId}|${section.trim().toLowerCase()}`;
};

interface ReportedMissingSectionsContextValue {
  /**
   * Section-level "requested": true only when that exact (venue config, section)
   * has a pending request. It does NOT fall back to a venue-map-level request — a
   * listing is "requested" only when its own section was reported.
   */
  isReported: (
    venueConfigId: number | null | undefined,
    section: string | null | undefined
  ) => boolean;
  /**
   * Venue-map-level "requested": true when a venue-map request (incorrect /
   * missing map, i.e. no section) exists for this venue config OR this event.
   * Section name is intentionally ignored at this level.
   */
  isMapReported: (
    venueConfigId: number | null | undefined,
    eventId: number | null | undefined
  ) => boolean;
  /** Optimistically mark a (venue config, section) as reported (POS-9306 FR7). */
  markReported: (
    venueConfigId: number | null | undefined,
    section: string | null | undefined
  ) => void;
  /** Optimistically mark a venue-map (venue config + event) as reported. */
  markMapReported: (
    venueConfigId: number | null | undefined,
    eventId: number | null | undefined
  ) => void;
}

const ReportedMissingSectionsContext =
  createContext<ReportedMissingSectionsContextValue>({
    isReported: () => false,
    isMapReported: () => false,
    markReported: () => {},
    markMapReported: () => {},
  });

export const useReportedMissingSections = () =>
  useContext(ReportedMissingSectionsContext);

/**
 * Tracks sections with a pending missing-section report so their unmapped
 * listings render as "requested" instead of "unmapped" (POS-9306 FR2/FR7). The
 * set is seeded from the backend (durable across reloads and shared across the
 * account's sessions) and updated optimistically on submit.
 */
export const ReportedMissingSectionsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const hasFeature = useUserHasFeature(Feature.CatalogSupport);
  const { activeAccountWebClientConfig } = useAppContext();
  const [reported, setReported] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  // Venue configs with a venue-map-level (null-section) report. Used by the
  // map-level action, never by the per-section listing check.
  const [reportedConfigs, setReportedConfigs] = useState<ReadonlySet<number>>(
    () => new Set()
  );
  // Events with a venue-map-level (null-section) report.
  const [reportedEvents, setReportedEvents] = useState<ReadonlySet<number>>(
    () => new Set()
  );

  const seedQuery = useQuery({
    queryKey: [
      'CatalogReportClient.getReportedSections',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: () =>
      new CatalogReportClient(
        activeAccountWebClientConfig
      ).getReportedSections(),
    enabled: hasFeature,
    refetchOnWindowFocus: false,
  });

  const seedData = seedQuery.data;
  useEffect(() => {
    if (!seedData) {
      return;
    }
    const nextSections = new Set<string>();
    const nextConfigs = new Set<number>();
    const nextEvents = new Set<number>();
    seedData.forEach((r) => {
      if (r.sectionName == null || r.sectionName.trim() === '') {
        // Venue-map-level report (no section): index by venue config and event.
        if (r.venueConfigId != null) {
          nextConfigs.add(r.venueConfigId);
        }
        if (r.viagogoEventId != null) {
          nextEvents.add(r.viagogoEventId);
        }
        return;
      }
      const key = sectionKey(r.venueConfigId, r.sectionName);
      if (key) {
        nextSections.add(key);
      }
    });
    setReported(nextSections);
    setReportedConfigs(nextConfigs);
    setReportedEvents(nextEvents);
  }, [seedData]);

  const markReported = useCallback(
    (
      venueConfigId: number | null | undefined,
      section: string | null | undefined
    ) => {
      const key = sectionKey(venueConfigId, section);
      if (key) {
        setReported((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
      }
    },
    []
  );

  const markMapReported = useCallback(
    (
      venueConfigId: number | null | undefined,
      eventId: number | null | undefined
    ) => {
      if (venueConfigId != null) {
        setReportedConfigs((prev) =>
          prev.has(venueConfigId) ? prev : new Set(prev).add(venueConfigId)
        );
      }
      if (eventId != null) {
        setReportedEvents((prev) =>
          prev.has(eventId) ? prev : new Set(prev).add(eventId)
        );
      }
    },
    []
  );

  const isReported = useCallback(
    (
      venueConfigId: number | null | undefined,
      section: string | null | undefined
    ) => {
      // Section-level check: a listing is "requested" only when its own section
      // has a pending request — never because the whole venue map was reported.
      const key = sectionKey(venueConfigId, section);
      return key != null && reported.has(key);
    },
    [reported]
  );

  const isMapReported = useCallback(
    (
      venueConfigId: number | null | undefined,
      eventId: number | null | undefined
    ) =>
      (venueConfigId != null && reportedConfigs.has(venueConfigId)) ||
      (eventId != null && reportedEvents.has(eventId)),
    [reportedConfigs, reportedEvents]
  );

  const value = useMemo(
    () => ({ isReported, isMapReported, markReported, markMapReported }),
    [isReported, isMapReported, markReported, markMapReported]
  );

  return (
    <ReportedMissingSectionsContext.Provider value={value}>
      {children}
    </ReportedMissingSectionsContext.Provider>
  );
};
